import React from 'react';
import { Box, Link as MuiLink, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { supportArticleRoute } from '@uplift/constants';
import {
  ArticleBulletList,
  ArticleParagraph,
  ArticleSection,
  ArticleSubsection,
  ArticleTable,
  useSupportArticleStyles,
} from './article-shared';
import { SUPPORT_ARTICLE_SLUGS } from './slugs';

const FORMULARY_ROWS: [string, string, string][] = [
  [ 'Diphenhydramine (Benadryl, Banophen)', 'Sedating antihistamine; occasional sleeplessness', 'Antihistamine Drugs' ],
  [ 'Hydroxyzine (Atarax, Vistaril)', 'Sedating antihistamine; anxiety symptoms', 'Antihistamine Drugs' ],
  [ 'Benztropine (Cogentin)', 'Anticholinergic; medication-induced parkinsonism and dystonia', 'Autonomic Drugs' ],
  [
    'Nicotine patches; nicotine gum or lozenges (Nicorette); varenicline (Chantix)',
    'Tobacco-cessation treatment',
    'Autonomic Drugs',
  ],
  [
    'Propranolol (Inderal)',
    'Performance-related anxiety; antipsychotic-induced akathisia; lithium-associated tremor (off-label)',
    'Cardiovascular Drugs',
  ],
  [
    'Clonidine extended-release (Kapvay); guanfacine extended-release (Intuniv)',
    'Non-stimulant ADHD treatment; adult use is off-label',
    'Cardiovascular Drugs',
  ],
  [ 'Prazosin (Minipress)', 'PTSD-related nightmares (off-label)', 'Cardiovascular Drugs' ],
  [ 'Sildenafil (Viagra); tadalafil (Cialis)', 'Erectile dysfunction', 'Cardiovascular Drugs' ],
  [
    'Amitriptyline; amoxapine (Asendin); bupropion (Wellbutrin); dextromethorphan–bupropion (Auvelity); citalopram (Celexa); clomipramine (Anafranil); desipramine (Norpramin); desvenlafaxine (Pristiq); doxepin; duloxetine (Cymbalta); escitalopram (Lexapro); fluoxetine (Prozac); fluvoxamine (Luvox); imipramine (Tofranil); mirtazapine (Remeron); nortriptyline (Pamelor, Aventyl); paroxetine (Paxil); selegiline transdermal (Emsam); sertraline (Zoloft); trazodone (Desyrel); venlafaxine (Effexor); vilazodone (Viibryd); vortioxetine (Trintellix)',
    'Antidepressants; depression and other medication-specific psychiatric uses',
    'Central Nervous System Agents',
  ],
  [
    'Oral aripiprazole (Abilify); brexpiprazole (Rexulti); cariprazine (Vraylar); lurasidone (Latuda); olanzapine (Zyprexa); paliperidone (Invega); quetiapine (Seroquel); risperidone (Risperdal); ziprasidone (Geodon)',
    'Antipsychotics; psychotic or bipolar disorders, or adjunctive depression treatment',
    'Central Nervous System Agents',
  ],
  [
    'Aripiprazole extended-release injectable (Abilify Maintena)',
    'Long-acting antipsychotic; schizophrenia and bipolar I maintenance monotherapy',
    'Central Nervous System Agents',
  ],
  [
    'Haloperidol decanoate injection (Haldol Decanoate)',
    'Long-acting antipsychotic; schizophrenia',
    'Central Nervous System Agents',
  ],
  [
    'Paliperidone palmitate extended-release injectable (Invega Sustenna)',
    'Long-acting antipsychotic; schizophrenia and schizoaffective disorder',
    'Central Nervous System Agents',
  ],
  [
    'Carbamazepine (Tegretol); lamotrigine (Lamictal); lithium; valproic acid; divalproex (Depakote)',
    'Mood stabilizers and related agents; bipolar disorder, with roles varying by medication',
    'Central Nervous System Agents',
  ],
  [
    'Oxcarbazepine (Trileptal); topiramate (Topamax)',
    'Anticonvulsants; selected off-label psychiatric uses',
    'Central Nervous System Agents',
  ],
  [
    'Gabapentin (Neurontin)',
    'Selected anxiety symptoms (off-label); state controlled-substance restrictions apply',
    'Central Nervous System Agents',
  ],
  [ 'Buspirone (Buspar)', 'Anxiety treatment', 'Central Nervous System Agents' ],
  [
    'Atomoxetine (Strattera); viloxazine extended-release (Qelbree)',
    'Non-stimulant ADHD treatment',
    'Central Nervous System Agents',
  ],
  [ 'Ramelteon (Rozerem)', 'Sleep-onset insomnia', 'Central Nervous System Agents' ],
  [
    'Naltrexone, oral',
    'Opioid antagonist; alcohol use disorder and opioid-relapse prevention after detox',
    'Central Nervous System Agents',
  ],
  [
    'Naltrexone extended-release injectable (Vivitrol)',
    'Opioid antagonist; alcohol use disorder and opioid-relapse prevention after detox',
    'Central Nervous System Agents',
  ],
  [
    'Ibuprofen (Advil, Motrin)',
    'Nonsteroidal anti-inflammatory; short-term pain relief',
    'Central Nervous System Agents',
  ],
  [
    'Ondansetron (Zofran)',
    'Antiemetic; medication-associated nausea, including off-label use',
    'Gastrointestinal Drugs',
  ],
  [ 'Liothyronine, oral (Cytomel)', 'Antidepressant augmentation (off-label)', 'Hormones and Synthetic Substitutes' ],
  [
    'Metformin',
    'Antipsychotic-associated weight gain and metabolic effects (off-label)',
    'Hormones and Synthetic Substitutes',
  ],
  [
    'Vitamin B12; folate/folic acid; cholecalciferol (vitamin D3); ergocalciferol (vitamin D2); thiamine (vitamin B1); multivitamins; prenatal vitamins',
    'Vitamin replacement and supplementation',
    'Vitamins',
  ],
  [
    'Disulfiram (Antabuse)',
    'Alcohol deterrent; supports abstinence in alcohol use disorder',
    'Antidote Therapeutics',
  ],
  [ 'Melatonin', 'Dietary supplement; sleep-wake timing support', 'Supplements' ],
];

export const FormularyArticle: React.FC = () => {
  const { classes } = useSupportArticleStyles();

  return (
    <>
      <ArticleSection title="Formulary Drugs">
        <ArticleParagraph>
          Medications supported in BetterHelp Psychiatry, with grouping informed by AHFS drug classification. Common
          uses are examples, include some off-label uses, and may vary by medication, formulation, and patient age.
        </ArticleParagraph>
        <ArticleTable
          headers={[ 'Medications', 'Common uses', 'Grouping' ]}
          rows={FORMULARY_ROWS}
          codeFirstColumn={false}
          columnWidths={[ '40%', '38%', '22%' ]}
        />
      </ArticleSection>

      <ArticleSection title="Before You Prescribe">
        <ArticleBulletList
          items={[
            <>
              Read this formulary alongside the{' '}
              <MuiLink
                component={Link}
                to={supportArticleRoute.createLink({ slug: SUPPORT_ARTICLE_SLUGS.prescribingPolicy })}
              >
                Prescribing Policy
              </MuiLink>
              .
            </>,
            'Practice within your state license, professional scope, and any applicable collaborative or supervisory agreement. This formulary does not expand your prescribing authority.',
            'No controlled substances on the platform, including gabapentin if state law classifies it as controlled.',
            'Listing a drug category does not mean we support use of every medication within it.',
            'Brand names are for identification. Brand or generic equivalents are appropriate based on clinical judgement.',
            'Listing does not guarantee availability, insurance coverage, or prior authorization approval.',
          ]}
        />

        <ArticleSubsection title="Injectables">
          <ArticleBulletList
            items={[
              'Before prescribing, confirm a plan for a qualified, authorized healthcare professional to administer the medication at a pharmacy, clinic, or similar setting. The injectables listed here are not for self-administration.',
              'Coordinate initiation, monitoring, and follow-up for the specific product, and document when and where each injection is given.',
              'Before starting naltrexone, assess opioid exposure and confirm that any required opioid-free interval has been met.',
            ]}
          />
        </ArticleSubsection>

        <ArticleParagraph>
          Questions about unlisted medications, formulations, or scope? Contact BetterHelp Psychiatry Support.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleSection title="Approval">
        <ArticleParagraph>
          Uplift Health Technologies, Inc., Uplift Behavioral Health, P.C., Uplift Behavioral Health NJ, P.C., Shine
          Medical Providers, P.C., Shine Medical of CA, P.C., Shine Medical of NJ, P.C., and Shine Medical, P.C., and
          BetterHelp, delivering care through the BetterHelp Telehealth Platforms
        </ArticleParagraph>
        <Box className={classes.metaRow}>
          <Typography variant="body2"><strong>Original Approval Date:</strong> 9/3/2025</Typography>
          <Typography variant="body2"><strong>Approved By:</strong> Compliance Team</Typography>
        </Box>
      </ArticleSection>
    </>
  );
};
